import type { ReactNode } from 'react';
import type { ClientRecipient } from '../types';
import { Avatar, Card, MicroLabel, SectionHead, cn } from '../ui/primitives';

/**
 * The settings section: grouped rows of label, explanation, and one control.
 *
 * ── Why every row carries a description ──
 *
 * These are defaults that silently shape money — a margin applied to every new
 * quote line, a floor below which the client can't be invoiced. A bare label
 * ("Invoice minimum") next to a number tells an admin nothing about what
 * changing it does, and this is the page where someone changes it once a year
 * and needs to be right. The description is a required prop for that reason.
 *
 * ── What the package does NOT own ──
 *
 * The controls. Toggles, selects and number inputs are app components wired to
 * app mutations, with app-side permissions and loading states — the row owns
 * where the control sits and nothing about what it does. That is also what lets
 * the org app render the same settings read-only by passing plain text where
 * the admin passes a select.
 */

export function ClientSettings({
	children,
	meta,
	action,
}: {
	children: ReactNode;
	meta?: ReactNode;
	action?: ReactNode;
}) {
	return (
		<>
			<SectionHead
				title="Settings"
				meta={meta ?? 'defaults for new quotes & orders · individual quotes can override'}
				action={action}
			/>
			<div className="flex flex-col gap-3.5">{children}</div>
		</>
	);
}

/**
 * One titled card of related settings.
 *
 * The header carries the group's NAME and its caveat and nothing else. Saving
 * lives in `footer`, at the bottom of the card where the rows it writes are —
 * a Save button in the header sat inline with the caveat text, on a baseline
 * row meant for type, and collided with it the moment the group went dirty.
 */
export function ClientSettingsGroup({
	title,
	note,
	action,
	footer,
	children,
}: {
	title: string;
	/** The caveat that belongs to the whole group — "snapshotted to each order
	 * when accepted". Worth saying once here rather than on every row. */
	note?: ReactNode;
	/** A header control that is NOT a save — a link out, a read-only marker. */
	action?: ReactNode;
	/** The group's save bar. Rendered under the rows, only when there is one. */
	footer?: ReactNode;
	children: ReactNode;
}) {
	return (
		<Card>
			<div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-line-1 px-5 py-3.5">
				<MicroLabel className="text-text-2">{title}</MicroLabel>
				{note && <span className="text-2xs text-text-4">{note}</span>}
				{action && <div className="ml-auto shrink-0">{action}</div>}
			</div>
			{children}
			{footer && (
				<div className="border-t border-line-1 bg-surface-fill px-5 py-3">{footer}</div>
			)}
		</Card>
	);
}

export function ClientSettingsRow({
	label,
	description,
	tag,
	children,
	last,
}: {
	label: string;
	/** Required — see the note at the top of this file. */
	description: ReactNode;
	/** A marker beside the label, for a value that syncs somewhere else. */
	tag?: ReactNode;
	/** The control. The app's. */
	children?: ReactNode;
	last?: boolean;
}) {
	return (
		<div
			className={cn(
				'flex flex-wrap items-center gap-x-6 gap-y-2.5 px-5 py-3.5',
				!last && 'border-b border-line-1'
			)}
		>
			<div className="min-w-0 flex-[1_1_16rem]">
				<div className="flex items-center gap-2 text-xs font-medium text-text-1">
					{label}
					{tag}
				</div>
				<div className="mt-0.5 text-2xs leading-relaxed text-text-3 [text-wrap:pretty]">
					{description}
				</div>
			</div>
			{children && <div className="ml-auto flex shrink-0 justify-end">{children}</div>}
		</div>
	);
}

/**
 * Who receives this client's invoices.
 *
 * Two kinds sit in one list: a contact of the client, and a bare external
 * address — their bookkeeper — who is not a contact and never will be. The
 * external one is drawn with a dashed mark rather than an avatar, because
 * "there is no person behind this address" is the useful distinction when
 * someone is deciding whether an invoice went to the right place.
 */
export function ClientRecipients({
	recipients,
	action,
	renderRecipientAction,
	emptyHint,
}: {
	recipients: ClientRecipient[];
	/** "Add recipient" — the app's. */
	action?: ReactNode;
	renderRecipientAction?: (recipient: ClientRecipient) => ReactNode;
	emptyHint?: string;
}) {
	return (
		<div className="px-5 py-3.5">
			<div className="mb-2.5 flex flex-wrap items-center gap-3">
				<div className="min-w-0 flex-1">
					<div className="text-xs font-medium text-text-1">Invoice recipients</div>
					<div className="mt-0.5 text-2xs text-text-3">
						Contacts or external addresses that receive this client&rsquo;s invoices.
					</div>
				</div>
				{action && <div className="shrink-0">{action}</div>}
			</div>

			<div className="overflow-hidden rounded-[var(--r-2)] border border-line-1">
				{recipients.length === 0 ? (
					<div className="p-3 text-2xs text-text-4">
						{emptyHint ?? 'No recipients yet — invoices go to the main email.'}
					</div>
				) : (
					recipients.map((recipient, i) => (
						<div
							key={recipient.id}
							className={cn(
								'flex items-center gap-2.5 bg-surface px-3 py-2',
								i < recipients.length - 1 && 'border-b border-line-1'
							)}
						>
							{recipient.isExternal ? (
								<span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dashed border-line-2 text-2xs text-text-4">
									@
								</span>
							) : (
								<Avatar name={recipient.name} size={24} className="rounded-full" />
							)}

							<span className="truncate text-xs text-text-1">
								{recipient.name || recipient.email}
							</span>
							{recipient.name && (
								<span className="hidden truncate font-mono text-2xs text-text-4 sm:block">
									{recipient.email}
								</span>
							)}

							<span className="ml-auto shrink-0 text-2xs text-text-4">
								{recipient.isExternal ? 'External' : 'Contact'}
							</span>
							{renderRecipientAction && (
								<span className="shrink-0">{renderRecipientAction(recipient)}</span>
							)}
						</div>
					))
				)}
			</div>
		</div>
	);
}
